import React, { useEffect, useRef, useState } from "react";
import { GoogleMap, useJsApiLoader, MarkerF } from "@react-google-maps/api";
import { Search, LocateFixed, Plus, X, MapPin, Loader2, Clock, Coins } from "lucide-react";
import api from "../../api";
import { useLang } from "../../context/LanguageContext";
import { useToast } from "../../hooks/use-toast";
import { CURRENCIES, symbolFor } from "../../utils/currency";

const GKEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const DEFAULT = { lat: 10.6549, lng: -61.5019 };

export const DealLocationPicker = ({ branches, onChange, defaultCenter, defaultHours = "", showHours = false, showCurrency = false, showPrice = false, baseCurrency = "", testId = "location-picker" }) => {
  const { t } = useLang();
  const { isLoaded } = useJsApiLoader({ id: "gmap-script", googleMapsApiKey: GKEY || "" });
  const { toast } = useToast();
  const [active, setActive] = useState(0);
  const [q, setQ] = useState("");
  const [searching, setSearching] = useState(false);
  const [locating, setLocating] = useState(false);
  const [named, setNamed] = useState(null); // { idx, address } — last auto-named pin, shown for confirmation
  const mapRef = useRef(null);
  const addrRefs = useRef({});
  const branchesRef = useRef(branches);
  useEffect(() => { branchesRef.current = branches; }, [branches]);

  const activeBranch = branches[active];
  const center = activeBranch && activeBranch.lat != null
    ? { lat: activeBranch.lat, lng: activeBranch.lng }
    : (branches[0]?.lat != null ? { lat: branches[0].lat, lng: branches[0].lng } : (defaultCenter || DEFAULT));

  const patch = (idx, obj) => onChange(branches.map((b, i) => (i === idx ? { ...b, ...obj } : b)));

  const dropAt = (lat, lng, address) => {
    const obj = { lat, lng };
    if (address != null) obj.address = address;
    if (!branches.length) {
      onChange([{ label: "", address: address || "", hours: defaultHours, lat, lng }]);
      setActive(0);
    } else {
      patch(active, obj);
    }
    if (mapRef.current) { mapRef.current.panTo({ lat, lng }); }
  };

  const addBranch = () => {
    const c = mapRef.current ? { lat: mapRef.current.getCenter().lat(), lng: mapRef.current.getCenter().lng() } : center;
    onChange([...branches, { label: "", address: "", hours: defaultHours, lat: c.lat, lng: c.lng }]);
    setActive(branches.length);
  };

  const removeBranch = (idx) => {
    const next = branches.filter((_, i) => i !== idx);
    onChange(next);
    setActive((a) => Math.max(0, Math.min(a, next.length - 1)));
  };

  const search = async () => {
    if (!q.trim()) return;
    setSearching(true);
    try {
      const r = await api.get("/geocode", { params: { q } });
      dropAt(r.data.lat, r.data.lng, r.data.formatted || q);
      if (mapRef.current) { mapRef.current.panTo({ lat: r.data.lat, lng: r.data.lng }); mapRef.current.setZoom(15); }
    } catch {
      toast({ title: t("dlp.placeNotFound"), description: t("dlp.tryADifferentAddress") });
    } finally { setSearching(false); }
  };

  const useGps = () => {
    if (!navigator.geolocation) { toast({ title: t("dlp.gpsUnavailable"), description: t("dlp.searchAnAddressInstead") }); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        dropAndName(pos.coords.latitude, pos.coords.longitude);
        if (mapRef.current) mapRef.current.setZoom(15);
        setLocating(false);
        toast({ title: t("dlp.pinnedYourLocation") });
      },
      () => { toast({ title: t("dlp.couldnTGetLocation"), description: t("dlp.allowLocationAccessOr") }); setLocating(false); },
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };

  // Name the pin from its coordinates so the deal never inherits a stale profile city.
  // Tapping the map / using GPS means "this is a different spot", so any stale name is cleared first.
  const dropAndName = async (lat, lng) => {
    dropAt(lat, lng, "");
    const idx = branches.length ? active : 0;
    try {
      const { data } = await api.get("/reverse-geocode", { params: { lat, lng } });
      if (data?.formatted) { patch2(idx, lat, lng, data.formatted); setNamed({ idx, address: data.formatted }); }
    } catch { /* keep the pin even if naming fails */ }
  };

  // Patch by coords so we don't clobber a pin the user moved while geocoding was in flight.
  // Reads the ref (not the closed-over prop) so the in-flight geocode can't revert the new pin.
  const patch2 = (idx, lat, lng, address) => {
    const list = branchesRef.current.length ? branchesRef.current : [{ label: "", address: "", hours: defaultHours, lat, lng }];
    onChange(list.map((b, i) => (i === idx && b.lat === lat && b.lng === lng && !(b.address || "").trim() ? { ...b, address } : b)));
  };

  const onMapClick = (e) => dropAndName(e.latLng.lat(), e.latLng.lng());

  const inputCls = "flex-1 border border-border rounded-xl px-4 py-2.5 text-sm bg-transparent outline-none focus:border-[#16a34a] text-foreground";

  return (
    <div className="space-y-3" data-testid={testId}>
      <div className="flex flex-wrap gap-2">
        <div className="flex flex-1 min-w-[220px] items-center gap-2 border border-border rounded-xl px-3 bg-transparent focus-within:border-[#16a34a]">
          <Search size={16} className="text-[#16a34a]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); search(); } }} placeholder={t("picker.searchPh")} className="flex-1 py-2.5 text-sm bg-transparent outline-none text-foreground placeholder:text-muted-foreground" data-testid="picker-search-input" />
          <button type="button" onClick={search} disabled={searching} className="text-sm font-semibold text-[#16a34a] disabled:opacity-60" data-testid="picker-search-btn">{searching ? <Loader2 size={15} className="animate-spin" /> : t("picker.find")}</button>
        </div>
        <button type="button" onClick={useGps} disabled={locating} className="inline-flex items-center gap-2 text-sm font-semibold text-[#16a34a] bg-[#e7f6ec] dark:bg-[#16341f] hover:bg-[#d6efdd] disabled:opacity-60 px-4 py-2.5 rounded-xl transition-colors" data-testid="picker-gps-btn">
          {locating ? <Loader2 size={15} className="animate-spin" /> : <LocateFixed size={15} />} {t("picker.gps")}
        </button>
      </div>

      <div className="rounded-xl overflow-hidden border border-border">
        {!GKEY ? (
          <div className="h-72 flex items-center justify-center bg-secondary text-muted-foreground text-sm">{t("dlp.mapUnavailableNoApi")}</div>
        ) : !isLoaded ? (
          <div className="h-72 flex items-center justify-center bg-secondary text-muted-foreground"><Loader2 className="animate-spin mr-2" /> {t("dlp.loadingMap")}</div>
        ) : (
          <GoogleMap
            mapContainerStyle={{ width: "100%", height: "288px" }}
            center={center}
            zoom={branches.length ? 13 : 10}
            onLoad={(m) => { mapRef.current = m; }}
            onClick={onMapClick}
            options={{ streetViewControl: false, mapTypeControl: false, fullscreenControl: false, clickableIcons: false }}
          >
            {branches.map((b, i) => (
              b.lat != null && (
                <MarkerF
                  key={i}
                  position={{ lat: b.lat, lng: b.lng }}
                  draggable
                  label={branches.length > 1 ? { text: String(i + 1), color: "#ffffff", fontWeight: "700" } : undefined}
                  onDragEnd={(e) => patch(i, { lat: e.latLng.lat(), lng: e.latLng.lng() })}
                  onClick={() => setActive(i)}
                  icon={i === active && window.google ? {
                    path: window.google.maps.SymbolPath.BACKWARD_CLOSED_ARROW,
                    scale: 6, fillColor: "#16a34a", fillOpacity: 1, strokeColor: "#fff", strokeWeight: 2,
                  } : undefined}
                />
              )
            ))}
          </GoogleMap>
        )}
      </div>
      {named && (branches[named.idx]?.address || "") === named.address && (
        <div className="rounded-xl border border-[#16a34a]/40 bg-[#e7f6ec] dark:bg-[#16341f] p-3 flex items-start gap-2.5" data-testid="pin-autoname-banner">
          <MapPin size={15} className="text-[#16a34a] shrink-0 mt-0.5" />
          <div className="min-w-0 flex-1">
            <p className="text-xs text-foreground">{t("picker.namedPre")} <span className="font-bold" data-testid="pin-autoname-value">{named.address}</span></p>
            <button type="button" onClick={() => { setActive(named.idx); const el = addrRefs.current[named.idx]; if (el) { el.focus(); el.select(); } }} data-testid="pin-autoname-edit" className="text-xs font-bold text-[#16a34a] hover:underline mt-0.5">{t("picker.editName")}</button>
          </div>
          <button type="button" onClick={() => setNamed(null)} data-testid="pin-autoname-dismiss" className="h-6 w-6 rounded-full hover:bg-black/10 flex items-center justify-center text-muted-foreground shrink-0" aria-label="Dismiss"><X size={13} /></button>
        </div>
      )}
      <p className="text-xs text-muted-foreground flex items-center gap-1.5"><MapPin size={12} className="text-[#16a34a]" /> {t("picker.hint")}</p>

      <div className="space-y-2">
        {branches.map((b, i) => (
          <div key={i} onClick={() => setActive(i)} className={`rounded-xl border p-3 cursor-pointer transition-colors ${i === active ? "border-[#16a34a] bg-[#e7f6ec] dark:bg-[#16341f]" : "border-border bg-card hover:border-[#16a34a]/50"}`} data-testid={`branch-card-${i}`}>
            <div className="flex items-center gap-2">
              <span className="h-6 w-6 shrink-0 rounded-full bg-[#16a34a] text-white text-xs font-bold flex items-center justify-center">{i + 1}</span>
              <input
                ref={(el) => { addrRefs.current[i] = el; }}
                value={b.address || ""}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => patch(i, { address: e.target.value })}
                placeholder={`Branch ${i + 1} address / name`}
                className="flex-1 bg-transparent outline-none text-sm text-foreground placeholder:text-muted-foreground"
                data-testid={`branch-address-${i}`}
              />
              <span className="text-[11px] text-muted-foreground hidden sm:block">{b.lat != null ? `${b.lat.toFixed(4)}, ${b.lng.toFixed(4)}` : "no pin"}</span>
              <button type="button" onClick={(e) => { e.stopPropagation(); removeBranch(i); }} className="h-7 w-7 rounded-full hover:bg-black/10 flex items-center justify-center text-muted-foreground" data-testid={`branch-remove-${i}`}><X size={15} /></button>
            </div>
            {showCurrency && (
            <div className="flex items-center gap-2 mt-2 pl-8">
              <Coins size={13} className="text-[#16a34a] shrink-0" />
              <select
                value={b.currency || ""}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => patch(i, { currency: e.target.value })}
                className="flex-1 bg-transparent outline-none text-xs text-muted-foreground"
                data-testid={`branch-currency-${i}`}
              >
                <option value="">{t("dlp.useBusinessDefaultCurrency")}</option>
                {CURRENCIES.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
              </select>
            </div>
            )}
            {showPrice && b.currency && b.currency !== baseCurrency && (
            <div className="flex flex-wrap items-center gap-2 mt-2 pl-8">
              <span className="text-[11px] text-muted-foreground">Local price ({symbolFor(b.currency)}) <span className="opacity-70">— leave blank to auto-convert</span></span>
              <input type="number" step="0.01" value={b.price ?? ""} onClick={(e) => e.stopPropagation()} onChange={(e) => patch(i, { price: e.target.value === "" ? null : parseFloat(e.target.value) })} placeholder="price" className="w-24 border border-border rounded-lg px-2 py-1 text-xs bg-transparent text-foreground outline-none focus:border-[#16a34a]" data-testid={`branch-price-${i}`} />
              <input type="number" step="0.01" value={b.was ?? ""} onClick={(e) => e.stopPropagation()} onChange={(e) => patch(i, { was: e.target.value === "" ? null : parseFloat(e.target.value) })} placeholder="was" className="w-24 border border-border rounded-lg px-2 py-1 text-xs bg-transparent text-foreground outline-none focus:border-[#16a34a]" data-testid={`branch-was-${i}`} />
            </div>
            )}
            {showHours && (
            <div className="flex items-center gap-2 mt-2 pl-8">
              <Clock size={13} className="text-[#16a34a] shrink-0" />
              <input
                value={b.hours || ""}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => patch(i, { hours: e.target.value })}
                placeholder={t("dlp.openingHoursOverrideOptional")}
                className="flex-1 bg-transparent outline-none text-xs text-muted-foreground placeholder:text-muted-foreground"
                data-testid={`branch-hours-${i}`}
              />
            </div>
            )}
          </div>
        ))}
        <button type="button" onClick={addBranch} className="inline-flex items-center gap-2 text-sm font-semibold text-[#16a34a] hover:underline" data-testid="picker-add-branch">
          <Plus size={16} /> {branches.length ? "Add another branch" : "Add a location pin"}
        </button>
      </div>
    </div>
  );
};

export default DealLocationPicker;
